/**
 * Article sidebar: table of contents, scroll progress, and read time.
 * Used by sections/main-blog-post.liquid.
 *
 * Mobile: full in-flow timeline until scrolled past, then a fixed compact bar
 * (active section + chevron) that expands to show the timeline again.
 */
class BlogPostSidebar extends HTMLElement {
  /** @type {HTMLElement | null} */
  #contentEl = null;

  /** @type {HTMLElement | null} */
  #tocSection = null;

  /** @type {HTMLElement | null} */
  #tocList = null;

  /** @type {HTMLButtonElement | null} */
  #tocTrigger = null;

  /** @type {HTMLElement | null} */
  #tocCurrent = null;

  /** @type {HTMLElement | null} */
  #tocSentinel = null;

  /** @type {HTMLElement | null} */
  #progressValue = null;

  /** @type {HTMLElement | null} */
  #progressFill = null;

  /** @type {HTMLElement | null} */
  #readTimeEl = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {IntersectionObserver | null} */
  #stuckObserver = null;

  /** @type {MediaQueryList | null} */
  #mobileMq = null;

  /** @type {HTMLElement[]} */
  #headings = [];

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number} */
  #tocNaturalHeight = 0;

  /** @type {number} */
  #ignoreDocClickUntil = 0;

  /**
   * Timestamp (ms) until which scroll-spy updates are ignored, so a clicked
   * link stays highlighted while the page smooth-scrolls to it.
   * @type {number}
   */
  #suppressSpyUntil = 0;

  /** @type {number} */
  #progressRaf = 0;

  /** @type {() => void} */
  #onScroll = () => {
    if (this.#progressRaf) return;

    this.#progressRaf = window.requestAnimationFrame(() => {
      this.#progressRaf = 0;
      this.#updateProgress();
      this.#syncActive();
    });
  };

  /** @type {(event: Event) => void} */
  #onTocClick = (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!(link instanceof HTMLAnchorElement) || !this.#tocList?.contains(link)) return;

    const id = link.getAttribute('href')?.slice(1);
    const target = id ? document.getElementById(id) : null;
    if (!target) return;

    event.preventDefault();

    // Highlight the clicked item straight away and pause the scroll-spy so it
    // isn't overridden by headings passing through while smooth-scrolling.
    this.#setActiveLink(id);
    this.#suppressSpyUntil = Date.now() + 700;
    this.#closeToc();

    target.focus({ preventScroll: true });
    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });

    if (history.replaceState) {
      history.replaceState(null, '', `#${id}`);
    }
  };

  /** @type {(event: Event) => void} */
  #onTriggerClick = () => {
    this.#ignoreDocClickUntil = Date.now() + 400;
    this.#toggleToc();
  };

  /** @type {(event: KeyboardEvent) => void} */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#tocSection?.classList.contains('is-open')) return;
    this.#closeToc();
    this.#tocTrigger?.focus();
  };

  /** @type {(event: Event) => void} */
  #onDocumentClick = (event) => {
    if (!this.#isStuck) return;
    if (!this.#tocSection?.classList.contains('is-open')) return;
    if (Date.now() < this.#ignoreDocClickUntil) return;
    if (!(event.target instanceof Node)) return;
    if (this.#tocSection.contains(event.target)) return;
    this.#closeToc();
  };

  /** @type {() => void} */
  #onMobileMqChange = () => {
    this.#measureTocHeight();
    if (!this.#isMobileToc()) {
      this.#setStuck(false);
    } else {
      this.#syncStuckFromSentinel();
    }
  };

  connectedCallback() {
    this.#contentEl = this.#resolveContent();
    this.#tocSection = this.querySelector('[data-toc-section]');
    this.#tocList = this.querySelector('[data-toc-list]');
    this.#tocTrigger = this.querySelector('[data-toc-trigger]');
    this.#tocCurrent = this.querySelector('[data-toc-current]');
    this.#tocSentinel = this.querySelector('[data-toc-sentinel]');
    this.#progressValue = this.querySelector('[data-progress-value]');
    this.#progressFill = this.querySelector('[data-progress-fill]');
    this.#readTimeEl = this.querySelector('[data-read-time]');

    if (!this.#contentEl) {
      this.hidden = true;
      return;
    }

    this.#markH2Numbering();
    const headings = this.#buildToc();
    this.#initReadTime();
    this.#updateProgress();
    this.#initMobileMq();
    this.#measureTocHeight();

    if (headings.length) {
      this.#initScrollSpy(headings);
      this.#initStuckObserver();
    } else {
      this.#tocSection?.setAttribute('hidden', '');
    }

    this.#tocList?.addEventListener('click', this.#onTocClick);
    this.#tocTrigger?.addEventListener('click', this.#onTriggerClick);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onMobileMqChange, { passive: true });
  }

  disconnectedCallback() {
    this.#tocList?.removeEventListener('click', this.#onTocClick);
    this.#tocTrigger?.removeEventListener('click', this.#onTriggerClick);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocumentClick);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    window.removeEventListener('resize', this.#onMobileMqChange);
    this.#mobileMq?.removeEventListener?.('change', this.#onMobileMqChange);
    this.#observer?.disconnect();
    this.#stuckObserver?.disconnect();
    if (this.#progressRaf) window.cancelAnimationFrame(this.#progressRaf);
  }

  /** @returns {HTMLElement | null} */
  #resolveContent() {
    const selector = this.dataset.contentSelector;
    if (selector) {
      const scoped = this.closest('.blog-post')?.querySelector(selector);
      if (scoped instanceof HTMLElement) return scoped;
    }

    const fallback = this.closest('.blog-post')?.querySelector('.blog-post-content');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  #initMobileMq() {
    const raw = this.dataset.mobileBreakpoint || '1024px';
    const px = Number.parseFloat(raw);
    const max = Number.isFinite(px) ? Math.max(px - 0.02, 0) : 1023.98;
    this.#mobileMq = window.matchMedia(`(max-width: ${max}px)`);
    this.#mobileMq.addEventListener?.('change', this.#onMobileMqChange);
  }

  /** @returns {boolean} */
  #isMobileToc() {
    return this.#mobileMq?.matches ?? false;
  }

  #measureTocHeight() {
    if (!this.#tocSection || this.#isStuck) return;
    this.#tocNaturalHeight = this.#tocSection.getBoundingClientRect().height;
  }

  #initStuckObserver() {
    if (!this.#tocSentinel) return;

    this.#stuckObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry || !this.#isMobileToc()) {
          this.#setStuck(false);
          return;
        }

        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.#setStuck(scrolledPast);
      },
      {
        threshold: 0,
        rootMargin: '0px',
      }
    );

    this.#stuckObserver.observe(this.#tocSentinel);
  }

  #syncStuckFromSentinel() {
    if (!this.#tocSentinel || !this.#isMobileToc()) {
      this.#setStuck(false);
      return;
    }

    const rect = this.#tocSentinel.getBoundingClientRect();
    this.#setStuck(rect.bottom < 0);
  }

  /** @param {boolean} stuck */
  #setStuck(stuck) {
    if (stuck === this.#isStuck) return;

    if (stuck) {
      this.#measureTocHeight();
      this.style.setProperty('--blog-post-toc-spacer', `${this.#tocNaturalHeight}px`);
    } else {
      this.#closeToc();
      this.style.removeProperty('--blog-post-toc-spacer');
    }

    this.#isStuck = stuck;
    this.classList.toggle('is-stuck', stuck);
  }

  /**
   * Auto circle+dash markers only for h2s that do not already start with a digit.
   * Pre-numbered titles (e.g. "2. Useful for All Ages") keep their own number and
   * do not advance the CSS counter.
   */
  #markH2Numbering() {
    if (!this.#contentEl) return;

    this.#contentEl.querySelectorAll('h2').forEach((heading) => {
      if (!(heading instanceof HTMLElement)) return;

      const text = heading.textContent?.trim() || '';
      if (/^\d/.test(text)) {
        heading.classList.remove('blog-post-h2--marker');
        heading.classList.add('blog-post-h2--pre-numbered');
      } else {
        heading.classList.remove('blog-post-h2--pre-numbered');
        heading.classList.add('blog-post-h2--marker');
      }
    });
  }

  /** @returns {HTMLElement[]} */
  #buildToc() {
    if (!this.#contentEl || !this.#tocList) return [];

    const headings = Array.from(this.#contentEl.querySelectorAll('h2, h3')).filter(
      (node) => node.textContent?.trim()
    );

    if (!headings.length) return [];

    const usedIds = new Set(
      Array.from(this.#contentEl.querySelectorAll('[id]'))
        .map((node) => node.id)
        .filter(Boolean)
    );

    const fragment = document.createDocumentFragment();
    let currentH2Item = null;

    headings.forEach((heading) => {
      if (!(heading instanceof HTMLElement)) return;

      if (!heading.id) {
        heading.id = this.#slugify(heading.textContent || '', usedIds);
        heading.setAttribute('tabindex', '-1');
      } else {
        usedIds.add(heading.id);
      }

      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent?.trim() || '';
      link.className = 'blog-post-sidebar__toc-link';

      const item = document.createElement('li');
      item.className = 'blog-post-sidebar__toc-item';
      item.dataset.headingId = heading.id;
      item.append(link);

      if (heading.tagName === 'H2') {
        currentH2Item = item;
        fragment.append(item);
        return;
      }

      if (currentH2Item) {
        let sublist = currentH2Item.querySelector('.blog-post-sidebar__toc-sublist');
        if (!sublist) {
          sublist = document.createElement('ol');
          sublist.className = 'blog-post-sidebar__toc-sublist';
          currentH2Item.append(sublist);
        }
        sublist.append(item);
        return;
      }

      fragment.append(item);
    });

    this.#tocList.replaceChildren(fragment);
    return headings;
  }

  /** @param {string} text @param {Set<string>} usedIds */
  #slugify(text, usedIds) {
    let base =
      text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-') || 'section';

    let id = base;
    let index = 1;

    while (usedIds.has(id)) {
      id = `${base}-${index}`;
      index += 1;
    }

    usedIds.add(id);
    return id;
  }

  /** @param {HTMLElement[]} headings */
  #initScrollSpy(headings) {
    this.#headings = headings;
    this.#syncActive();

    // The observer is only a lightweight trigger; the actual active heading is
    // resolved by position in #syncActive (also called on every scroll frame).
    this.#observer = new IntersectionObserver(() => this.#syncActive(), {
      rootMargin: '0px 0px -70% 0px',
      threshold: [0, 1],
    });

    headings.forEach((heading) => this.#observer?.observe(heading));
  }

  /**
   * Highlights the heading the reader is currently on: the last heading whose
   * top has scrolled above a reading line just beneath the sticky header. This
   * matches where anchored clicks land, so the clicked item stays active.
   */
  #syncActive() {
    if (!this.#headings.length || Date.now() < this.#suppressSpyUntil) return;

    const [firstHeading] = this.#headings;
    const offset = Number.parseFloat(getComputedStyle(firstHeading).scrollMarginTop) || 0;
    const readingLine = offset + 24;

    let activeId = firstHeading.id;
    for (const heading of this.#headings) {
      if (heading.getBoundingClientRect().top <= readingLine) {
        activeId = heading.id;
      } else {
        break;
      }
    }

    this.#setActiveLink(activeId);
  }

  /** @param {string} id */
  #setActiveLink(id) {
    this.querySelectorAll('.blog-post-sidebar__toc-item').forEach((item) => {
      const isActive = item.dataset.headingId === id;
      item.classList.toggle('is-active', isActive);

      if (isActive && this.#tocCurrent) {
        const link = item.querySelector('.blog-post-sidebar__toc-link');
        const label = link?.textContent?.trim();
        if (label) this.#tocCurrent.textContent = label;
      }
    });
  }

  #toggleToc() {
    if (this.#tocSection?.classList.contains('is-open')) {
      this.#closeToc();
      return;
    }
    this.#openToc();
  }

  #openToc() {
    this.#tocSection?.classList.add('is-open');
    this.#tocTrigger?.setAttribute('aria-expanded', 'true');
  }

  #closeToc() {
    this.#tocSection?.classList.remove('is-open');
    this.#tocTrigger?.setAttribute('aria-expanded', 'false');
  }

  #initReadTime() {
    if (!this.#readTimeEl || !this.#contentEl) return;

    const minutes = Number.parseInt(this.dataset.readMinutes || '', 10);
    const template = this.dataset.readTimeTemplate || '';

    if (!Number.isFinite(minutes) || minutes < 1 || !template) return;

    this.#readTimeEl.textContent = template.replace('__COUNT__', String(minutes));
  }

  #updateProgress() {
    if (!this.#contentEl || !this.#progressValue) return;

    const rect = this.#contentEl.getBoundingClientRect();
    const viewport = window.innerHeight;
    const contentTop = rect.top + window.scrollY;
    const contentHeight = this.#contentEl.offsetHeight;
    const scrollY = window.scrollY;
    const start = contentTop - viewport * 0.1;
    const end = contentTop + contentHeight - viewport * 0.5;
    const range = Math.max(end - start, 1);
    const progress = Math.min(100, Math.max(0, ((scrollY - start) / range) * 100));
    const rounded = Math.round(progress);

    this.#progressValue.textContent = `${rounded}%`;

    if (this.#progressFill) {
      this.#progressFill.style.width = `${progress}%`;
    }

    this.#progressValue.closest('[role="progressbar"]')?.setAttribute('aria-valuenow', String(rounded));
  }
}

if (!customElements.get('blog-post-sidebar')) {
  customElements.define('blog-post-sidebar', BlogPostSidebar);
}
